import React, { useEffect, useState } from 'react';
import { List, Form, Input, Button, Comment, Avatar, Typography, message, Divider, Collapse } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../../api';
import moment from 'moment';
import 'moment/locale/hy-am';
import 'moment/locale/ru';
import i18n from 'i18next';

const { TextArea } = Input;
const { Title } = Typography;
const { Panel } = Collapse;

const Comments = ({ quizId }) => {
    const { t } = useTranslation();
    const [comments, setComments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [storedAuthorName, setStoredAuthorName] = useState(localStorage.getItem('chat_user_name') || '');
    const [form] = Form.useForm();

    const fetchComments = async () => {
        setLoading(true);
        try {
            const res = await api.API.get(`api/quizes/${quizId}/comments`);
            setComments(Array.isArray(res) ? res : []);
        } catch (error) {
            console.error('Failed to fetch comments', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const lang = i18n.language === 'hy' ? 'hy-am' : i18n.language;
        moment.locale(lang);
        fetchComments();

        const handleUserInfoUpdate = () => {
            const name = localStorage.getItem('chat_user_name');
            if (name) {
                setStoredAuthorName(name);
                form.setFieldsValue({ author_name: name });
            }
        };
        window.addEventListener('user_info_updated', handleUserInfoUpdate);
        return () => window.removeEventListener('user_info_updated', handleUserInfoUpdate);
    }, [quizId, i18n.language]);

    const handleSubmit = async (values) => {
        if (!values.content) return;

        const authorName = values.author_name || storedAuthorName || 'Անանուն';

        setSubmitting(true);
        try {
            await api.API.post(`api/quizes/${quizId}/comments`, {
                content: values.content,
                author_name: authorName,
                honeypot: values.honeypot, 
            });
            message.success(t('comments.success'));
            form.resetFields(['content']);
            fetchComments();
        } catch (error) {
            if (error.response && error.response.status === 429) {
                message.error(t('comments.error_spam'));
            } else {
                message.error(error.response?.data?.message || (i18n.language === 'hy' ? 'Մեկնաբանությունն ավելացնելու սխալ' : 'Ошибка при добавлении комментария'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div style={{ marginTop: '30px', background: '#fff', padding: '20px', borderRadius: '8px' }}>
            <Divider />
            
            <Collapse ghost defaultActiveKey={['1']}>
                <Panel 
                    header={<Title level={4} style={{ margin: 0 }}>{t('comments.title')} ({comments.length})</Title>} 
                    key="1"
                >
                    <List
                        loading={loading}
                        className="comment-list"
                        itemLayout="horizontal"
                        dataSource={comments}
                        pagination={comments.length > 5 ? { pageSize: 5 } : false}
                        renderItem={(item) => (
                            <li>
                                <Comment
                                    author={item.author_name || (i18n.language === 'hy' ? 'Հյուր' : 'Гость')}
                                    avatar={<Avatar src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${item.author_name || 'user'}`} alt={item.author_name} />}
                                    content={item.content}
                                    datetime={moment(item.created_at).fromNow()}
                                />
                            </li>
                        )}
                    />
                </Panel>
            </Collapse>

            <Divider />

            <Form form={form} onFinish={handleSubmit} layout="vertical" initialValues={{ author_name: storedAuthorName }}>
                {!storedAuthorName && (
                    <Form.Item name="author_name" label={t('comments.name')}>
                        <Input placeholder={t('comments.placeholder_name')} />
                    </Form.Item>
                )}
                <Form.Item
                    name="content"
                    label={t('comments.content')}
                    rules={[{ required: true, message: t('comments.placeholder_content') }]}
                >
                    <TextArea rows={4} placeholder={t('comments.placeholder_content')} />
                </Form.Item>
                
                <Form.Item name="honeypot" style={{ display: 'none' }}>
                    <Input tabIndex="-1" autoComplete="off" />
                </Form.Item>

                <Form.Item>
                    <Button htmlType="submit" loading={submitting} type="primary">
                        {t('comments.submit')}
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
};

export default Comments;
